import {
  FormControl,
  FormLabel,
  Input,
  Select,
  Stack,
  Switch,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";

import FixedBar from "components/ui/FixedBar";
import SubmitButton from "components/ui/SubmitButton";
import { useSuccessDialogContext } from "components/ui/SuccessDialog/SuccessDialogContext";
import { UserRole } from "components/ui/UserRole/user-role.enum";
import { useMessages } from "hooks/useMessages";
import { Resolver, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { UserInputs, useSaveUser } from "../hooks/useSaveUser";

const validationSchema = Yup.object().shape({
  name: Yup.string().required(),
  email: Yup.string().required().email(),
  role: Yup.string().required(),
});

type UserFormProps = {
  currentUser?: any;
};

export default function UserForm({ currentUser }: UserFormProps) {
  const defaultValues: UserInputs = {
    name: currentUser?.name,
    email: currentUser?.email,
    role: currentUser?.role,
    active: currentUser?.active,
  };

  const navigate = useNavigate();
  const { openSuccessDialog } = useSuccessDialogContext();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isValid },
  } = useForm<UserInputs>({
    mode: "all",
    resolver: yupResolver(validationSchema) as Resolver<UserInputs>,
    defaultValues,
  });

  const { onError } = useMessages();

  const isActive = watch("active");

  const { onSaveUser, isPending } = useSaveUser({
    userId: currentUser?.id,
    onSuccess: ({ data: { user } }) => {
      reset();
      navigate("/usuarios");
      if (!currentUser) {
        openSuccessDialog({
          title: "Novo usuário cadastrado!",
          description: `Oriente o usuário ${user.name} a aceitar o convite enviado para ${user.email}`,
          actionButtonText: "Criar mais um usuário",
          actionButton: "/usuarios/novo",
          closeButtonText: "Tudo certo",
        });
      }
    },
    onError: (error) => onError(error?.response?.data?.message),
  });

  const onSubmit: SubmitHandler<UserInputs> = (data) => onSaveUser(data);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormControl isInvalid={!!errors.name} isRequired>
        <FormLabel>Nome</FormLabel>
        <Input {...register("name")} autoFocus />
      </FormControl>

      <Stack spacing="32px" direction="row">
        <FormControl isInvalid={!!errors.email} isRequired>
          <FormLabel>E-mail</FormLabel>
          <Input {...register("email", { required: true })} />
        </FormControl>
        <FormControl isInvalid={!!errors.role} isRequired>
          <FormLabel>Tipo de perfil</FormLabel>
          <Select
            placeholder="Selecione uma opção"
            {...register("role", { required: true })}
          >
            {Object.keys(UserRole).map((key) => (
              <option key={`opt-${key}`} value={key}>
                {UserRole[key]}
              </option>
            ))}
          </Select>
        </FormControl>
      </Stack>
      {currentUser && (
        <FormControl isInvalid={!!errors.active}>
          <FormLabel>Status</FormLabel>
          <Switch {...register("active")}>
            {isActive ? "Ativo" : "Inativo"}
          </Switch>
        </FormControl>
      )}

      <FixedBar>
        <SubmitButton isDisabled={isPending || !isValid} isLoading={isPending}>
          {!currentUser ? "Cadastrar" : "Atualizar"}
        </SubmitButton>
      </FixedBar>
    </form>
  );
}
